import { z } from 'zod';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  AssetsParamsSchema,
  AssetsQuerySchema,
  AssetsPreviewReturnSchema,
  ErrorResponseSchema,
  AssetsTrendQuerySchema,
  AssetsTrendResponseSchema,
  DomainTreeQuerySchema,
  DomainTreeResponseSchema,
  ApplicationEntityCountsQuerySchema,
  ApplicationEntityCountsResponseSchema,
} from './assets.schema';
import {
  RerunUnverifiedAssetBodySchema,
  RerunUnverifiedAssetResponseSchema,
  UnverifiedAssetDetailSchema,
  UnverifiedAssetParamsSchema,
  UnverifiedAssetProbeStatusResponseSchema,
} from './unverified-assets.schema';

export const ASSETS_PATH_NAME = 'assets';

export const AssetsContract = terraContract
  .prefix(`/${ASSETS_PATH_NAME}`)
  .router({
    searchAssetsPreview: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{applicationId}/preview/search',
        summary:
          'Search unified assets preview by application id using request body filters',
        description:
          'POST variant of unified assets preview that supports domain tree selection filters (host, pathPrefix, method) for server-side filtering.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: AssetsParamsSchema, body: AssetsQuerySchema }))
      .output(AssetsPreviewReturnSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getDomainTree: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/domain-tree',
        summary: 'Get application domain hierarchy tree',
        description:
          'Returns the URL-based domain hierarchy tree (domain → path → endpoint/webpage) built from all application endpoints and web pages.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({ params: AssetsParamsSchema, query: DomainTreeQuerySchema }),
      )
      .output(DomainTreeResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    countApplicationEntities: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/counts',
        summary: 'Count application entities without loading rows',
        description:
          'Cheap badge and tab totals. Supports assets typeCounts plus signals, findings, and traces totals. Findings totals default to published-only. Pass includeUnpublished=true with Execute Tests to include unpublished findings. Pass lifecycleState=all for All badges while lists stay Active.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: AssetsParamsSchema,
          query: ApplicationEntityCountsQuerySchema,
        }),
      )
      .output(ApplicationEntityCountsResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getUnverifiedAsset: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/unverified/{candidateId}',
        summary: 'Get unverified asset detail by endpoint candidate id',
        description:
          'Returns the drill-down view of an unverified endpoint candidate: origin, verification conclusion, and the probe trace composed from the recorded discovery and probing evidence.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: UnverifiedAssetParamsSchema }))
      .output(UnverifiedAssetDetailSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    deleteUnverifiedAsset: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{applicationId}/unverified/{candidateId}',
        summary: 'Delete unverified asset',
        description:
          'Permanently deletes an unverified endpoint candidate. This action cannot be undone.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: UnverifiedAssetParamsSchema }))
      .output(z.null())
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    rerunUnverifiedAsset: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{applicationId}/unverified/{candidateId}/rerun-probe',
        summary: 'Re-run probing for an unverified asset',
        description:
          'Starts a manual probe for a specific unverified endpoint candidate. Optional context fields guide the probing agent. Manual re-runs from this flow may pass probing guardrails that would normally restrict automatic probing.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: UnverifiedAssetParamsSchema,
          body: RerunUnverifiedAssetBodySchema.optional(),
        }),
      )
      .output(RerunUnverifiedAssetResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getUnverifiedAssetProbeStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/unverified/{candidateId}/probe-status',
        summary: 'Get unverified asset probe status',
        description:
          'Returns the current probe phase for an endpoint candidate, including the promoted verified endpoint id when probing succeeds.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: UnverifiedAssetParamsSchema }))
      .output(UnverifiedAssetProbeStatusResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getAssetsTrend: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/analytics/trend',
        summary: 'Get assets detection trend over time',
        description:
          'Returns the count of endpoints and web pages detected over time. Supports filtering by date range, environment, and business unit.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: AssetsTrendQuerySchema }))
      .output(AssetsTrendResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
  });
